import axios from '@/lib/axios';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  bookingQueryKeys,
  getBookingDetailRefetchInterval,
} from '@/lib/booking-query-keys';

export interface Cleaner {
  booked_cleaner_id: string;
  cleaner_id: string;
  no_of_hours: number;
  no_of_cleaners: number;
  assigned_date: string;
  cleaner_firstname: string;
  cleaner_lastname: string;
  cleaner_email: string;
  cleaner_phone: string;
  cleaner_profile_pic: string | null;
  has_review: boolean;
  rating: number | null;
  comments: string | null;
  tip_amount?: number | null;
  real_no_of_hours?: number | null;
}

export interface BookingDetail {
  booking_id: string;
  booking_date: string;
  booking_time: string;
  booking_date_time?: string | null;
  type_of_service: string;
  status:
    | 'pending'
    | 'accepted'
    | 'confirmed'
    | 'completed'
    | 'canceled'
    | 'upcoming'
    | 'ongoing'
    | 'requested';
  address: string;
  /** Apt/suite/unit from the booking address, if provided by the API. */
  apt_suite_unit?: string;
  city: string;
  state: string;
  zipcode?: string;
  country: string;
  customer_id?: string;
  customer_firstname?: string;
  customer_lastname?: string;
  customer_email?: string;
  customer_phone?: string;
  payment_id?: string;
  total_amount: string;
  /** Amount charged (completed bookings; extensions included when API provides it). */
  total_amount_charged?: number | string | null;
  payment_date: string;
  transaction_id?: string;
  cleaners: Cleaner[];
  real_no_of_hours?: number | null;
  /** Base duration in hours (detail endpoint). */
  duration_hours?: number | null;
  /** Extended hours added via booking extensions (detail endpoint). */
  additional_hours?: number | null;
  created_at?: string;
  description?: string;
  house_specs?: {
    bedrooms: number;
    bathrooms: number;
    square_feet: string;
  };
  payment_method?: string;
  /** Instant vs browse booking type. */
  booking_type?: string;
  no_of_hours?: number;
  hourly_rate?: number;
  booked_hours?: number;
  /** JobProgress actual start (ISO); used for ongoing countdown. */
  start_time?: string | null;
  /** Computed/actual end (ISO); fallback when start_time missing. */
  end_time?: string | null;
  address_id?: string | null;
  bedroom?: number;
  bathroom?: number;
  people?: number;
  pet?: number;
  pets?: number;
  cleaning_area?: string;
  house_desc?: string;
  house_rating?: number;
  latitude?: number | string | null;
  longitude?: number | string | null;
  payment_authorization_status?: string | null;
  payment_authorization_action_required?: boolean | null;
  /** Ongoing only: false when cleaner has another booking soon. */
  can_extend?: boolean | null;
  cancel_reason?: string | null;
  customer_cancel_reason_label?: string | null;
}

/**
 * Alias for `bookingQueryKeys.detail` — kept for backward compatibility with
 * existing importers. New code should use `bookingQueryKeys.detail` directly.
 */
export const bookingDetailQueryKey = bookingQueryKeys.detail;

/**
 * The detail endpoint may return `address` either as a flat string (list shape)
 * or as a nested `ResponseAddressModel` object containing `address`,
 * `apt_suite_unit`, `city`, `state`, `zipcode`, etc. Resolve a field from
 * whichever shape we receive, falling back to the top-level value.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveAddressField(raw: any, key: string): any {
  const addrObj = raw?.address;
  if (addrObj != null && typeof addrObj === 'object') {
    return addrObj[key] ?? raw[key];
  }
  return raw[key];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeBookingDetail(raw: any): BookingDetail {
  const address =
    typeof raw.address === 'string'
      ? raw.address
      : resolveAddressField(raw, 'address') || '';

  return {
    booking_id: raw.booking_id || raw.id || '',
    booking_date: raw.booking_date || '',
    booking_time: raw.booking_time || '',
    booking_date_time: raw.booking_date_time ?? null,
    type_of_service: raw.type_of_service || '',
    status: raw.status || 'pending',
    address,
    apt_suite_unit: resolveAddressField(raw, 'apt_suite_unit') || undefined,
    city: resolveAddressField(raw, 'city') || '',
    state: resolveAddressField(raw, 'state') || '',
    zipcode: resolveAddressField(raw, 'zipcode') || '',
    country: resolveAddressField(raw, 'country') || '',
    customer_id: raw.customer_id || '',
    customer_firstname: raw.customer_firstname || '',
    customer_lastname: raw.customer_lastname || '',
    customer_email: raw.customer_email || '',
    customer_phone: raw.customer_phone || '',
    payment_id: raw.payment_id || '',
    total_amount: raw.total_amount ?? '0',
    total_amount_charged: raw.total_amount_charged ?? null,
    payment_date: raw.payment_date || raw.created_at || '',
    transaction_id: raw.transaction_id || '',
    cleaners: (raw.cleaners || []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (c: any) => ({
        booked_cleaner_id: c.booked_cleaner_id || c.cleaner_id,
        cleaner_id: c.cleaner_id,
        no_of_hours: c.no_of_hours || 0,
        no_of_cleaners: c.no_of_cleaners || 1,
        assigned_date: c.assigned_date || '',
        cleaner_firstname: c.cleaner_firstname || '',
        cleaner_lastname: c.cleaner_lastname || '',
        cleaner_email: c.cleaner_email || '',
        cleaner_phone: c.cleaner_phone || '',
        cleaner_profile_pic: c.cleaner_profile_pic ?? null,
        has_review: c.has_review || false,
        rating: c.rating ?? null,
        comments: c.comments ?? null,
        tip_amount: c.tip_amount ?? null,
        real_no_of_hours: c.real_no_of_hours ?? null,
      })
    ),
    real_no_of_hours: raw.real_no_of_hours ?? null,
    duration_hours: raw.duration_hours ?? null,
    additional_hours: raw.additional_hours ?? null,
    created_at: raw.created_at || '',
    description: raw.description || raw.house_desc || '',
    house_specs:
      raw.house_specs ||
      (raw.bedroom != null
        ? {
            bedrooms: raw.bedroom,
            bathrooms: raw.bathroom,
            square_feet: raw.cleaning_area || '0 sq ft',
          }
        : undefined),
    payment_method: raw.payment_method || '',
    booking_type: raw.booking_type || '',
    no_of_hours: raw.no_of_hours,
    hourly_rate: raw.hourly_rate,
    booked_hours: raw.booked_hours,
    start_time: raw.start_time ?? null,
    end_time: raw.end_time ?? null,
    address_id: raw.address_id ?? null,
    bedroom: raw.bedroom ?? raw.house_specs?.bedrooms,
    bathroom: raw.bathroom ?? raw.house_specs?.bathrooms,
    people: raw.people,
    pet: raw.pet ?? raw.pets,
    pets: raw.pets ?? raw.pet,
    cleaning_area: raw.cleaning_area || '',
    house_desc: raw.house_desc || raw.description || '',
    house_rating: raw.house_rating,
    latitude: raw.latitude,
    longitude: raw.longitude,
    payment_authorization_status: raw.payment_authorization_status ?? null,
    payment_authorization_action_required:
      raw.payment_authorization_action_required ?? null,
    can_extend: raw.can_extend ?? null,
    cancel_reason: raw.cancel_reason ?? null,
    customer_cancel_reason_label: raw.customer_cancel_reason_label ?? null,
  };
}

export async function getBookingDetailQuery(
  bookingId: string
): Promise<BookingDetail> {
  const response = await axios.get(`/booking/${bookingId}/`);

  if (!response.data) {
    throw new Error('Booking detail data not found in response');
  }

  return normalizeBookingDetail(response.data);
}

export function useGetBookingDetailQuery(
  bookingId: string,
  options?: Partial<UseQueryOptions<BookingDetail>>
) {
  return useQuery({
    queryKey: bookingDetailQueryKey(bookingId),
    queryFn: () => getBookingDetailQuery(bookingId),
    retry: false,
    enabled: !!bookingId && bookingId.trim().length > 0,
    placeholderData: previous => previous,
    refetchInterval: query => getBookingDetailRefetchInterval(query.state.data),
    ...options,
  });
}
